import { getTiktokUserImages, TiktokUserImage, microwave } from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import { useAbortableEffect } from '@round/utils';
import { buildTiktokUserUrl, showNotification } from 'helpers';
import useCreatorPlanTiktokAudios from '../../../../InfluencerPlan/contexts/TiktokCreatorsContext/useCreatorPlanTiktokAudios';
import { TiktokInfluencerPlanAudio } from 'App.types';
import { UserData } from 'Modules/TikTok/components/TiktokUserBadge/TiktokUserBadge';
import { isNumber } from 'lodash';

type Params = {
    campaignId?: number;
    page: number;
    pageSize: number;
    ordering: microwave.GetInvitesParams['ordering'];
    isPosted: boolean;
    numberOfChaseEmailsSent?: number | undefined;
};

type HookConfig = {
    isDisabled?: boolean;
};

type BaseInviteData = {
    id: string;
    numberOfChaseEmailsSent: number;
    deadline: string;
    postCreateTime: string | null;
    lastContactedAt: string | null;
    postUrl: string | null;
    tiktokUserId?: number;
    audioId: number | null;
    influencerEmailAddress: string;
    influencerId: number;
};

export type TiktokPostInviteData = BaseInviteData & {
    userData: UserData;
    audio: TiktokInfluencerPlanAudio | undefined;
};

const mapMicrowaveInviteToBaseInvite = (
    invite: microwave.Invite,
    influencer?: microwave.MicrowaveInfluencer
): BaseInviteData => ({
    id: invite.id,
    influencerEmailAddress: invite.influencer_email_address,
    influencerId: invite.influencer_id,
    numberOfChaseEmailsSent: invite.chase_email_count,
    tiktokUserId: influencer?.tiktok_user_id,
    audioId: invite.tiktok_audio_id,
    deadline: invite.deadline,
    lastContactedAt: invite.last_contacted_at,
    postCreateTime: invite.post_create_time,
    postUrl: invite.post_url,
});

export default function useTiktokPostInvites(
    { page, pageSize, campaignId, ordering, isPosted, numberOfChaseEmailsSent }: Params,
    { isDisabled }: HookConfig = {}
) {
    const { influencerPlanAudios } = useCreatorPlanTiktokAudios();

    const [contactedCreators, setContactedCreators] = useState<BaseInviteData[]>([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [creatorsCount, setCreatorsCount] = useState(0);
    const [creatorsLoading, setCreatorsLoading] = useState(false);
    const [errorLoading, setErrorLoading] = useState(false);

    const [influencers, setInfluencers] = useState<microwave.MicrowaveInfluencer[]>([]);
    const [tiktokUserImages, setTiktokUserImages] = useState<TiktokUserImage[]>([]);

    const fetchMicrowaveInvites = useCallback(
        async (params: Params, requestInit?: RequestInit) => {
            try {
                setErrorLoading(false);
                setCreatorsLoading(true);
                const response = await microwave.getInvites(
                    {
                        page: params.page,
                        page_size: params.pageSize,
                        campaign_id: params.campaignId,
                        chase_email_count: params.numberOfChaseEmailsSent,
                        is_posted: params.isPosted,
                        ordering: params.ordering,
                    },
                    requestInit
                );

                if (response.status === 400 || response.status === 404) {
                    setErrorLoading(true);
                    return;
                }

                const influencerIds = response.data.results.map((item) => item.influencer_id);
                const fetchedInfluencerIds = influencers.map((item) => item.id);
                const influencersToFetch = influencerIds.filter((id) => !fetchedInfluencerIds.includes(id));
                let fetchedInfluencers: microwave.MicrowaveInfluencer[] = [];
                if (influencersToFetch.length) {
                    const influencersResponse = await microwave.getMaterializedMicrowaveInfluencers(
                        {
                            id: influencersToFetch.toString(),
                            page_size: influencersToFetch.length,
                        },
                        requestInit
                    );

                    fetchedInfluencers = influencersResponse.status === 200 ? influencersResponse.data.results : [];
                    setInfluencers((influencers) => influencers.concat(fetchedInfluencers));
                }

                setContactedCreators(
                    response.data.results.map((invite) =>
                        mapMicrowaveInviteToBaseInvite(
                            invite,
                            influencers.concat(fetchedInfluencers).find((i) => i.id === invite.influencer_id)
                        )
                    )
                );

                setCreatorsCount(response.data.count);
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setErrorLoading(true);
            } finally {
                setCreatorsLoading(false);
            }
        },
        [influencers]
    );

    useAbortableEffect(
        (signal) => {
            if (isInitialized) {
                return;
            }

            // to prevent from infinite loading state
            if (isDisabled) {
                setIsInitialized(true);
                return;
            }

            fetchMicrowaveInvites(
                { page, pageSize, ordering, numberOfChaseEmailsSent, campaignId, isPosted },
                { signal }
            );
        },
        [
            page,
            ordering,
            campaignId,
            pageSize,
            isPosted,
            numberOfChaseEmailsSent,
            fetchMicrowaveInvites,
            isInitialized,
            isDisabled,
        ]
    );

    useAbortableEffect(
        (signal) => {
            async function fetchUsers() {
                try {
                    const userIds = contactedCreators.map((item) => item.tiktokUserId).filter(isNumber);
                    const fetchedImagesUserIds = tiktokUserImages.map((image) => image.user_id);
                    const imagesToFetch = userIds.filter((id) => !fetchedImagesUserIds.includes(id));

                    if (imagesToFetch.length) {
                        const tiktokUserImages = await getTiktokUserImages(userIds, { signal });
                        setTiktokUserImages((images) => images.concat(tiktokUserImages));
                    }
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    showNotification('Could not fetch users data', 'error');
                }
            }

            fetchUsers();
        },
        [contactedCreators, tiktokUserImages]
    );

    const creators = useMemo(
        () =>
            contactedCreators.map<TiktokPostInviteData>((creator) => {
                const userImage = tiktokUserImages.find((image) => image.user_id === creator.tiktokUserId);
                const influencer = influencers.find((i) => i.id === creator.influencerId);
                return {
                    ...creator,
                    userData: {
                        profileUrl: influencer?.tiktok_user_unique_id
                            ? buildTiktokUserUrl(influencer?.tiktok_user_unique_id)
                            : '',
                        imageUrl: userImage?.avatar_thumb.cached_url || userImage?.avatar_thumb.original_url || '',
                        nickname: influencer?.tiktok_user_nickname || '-',
                        username: influencer?.tiktok_user_unique_id ? `@${influencer?.tiktok_user_unique_id}` : '-',
                    },
                    audio: influencerPlanAudios.find((audio) => audio.audio_id === creator.audioId),
                };
            }),
        [contactedCreators, tiktokUserImages, influencers, influencerPlanAudios]
    );

    const updatePostInvite = useCallback(
        async (
            id: string,
            data: Pick<microwave.PatchInviteData, 'deadline'>
        ): Promise<{ success: boolean; error?: string }> => {
            const response = await microwave.patchInvite(id, data);
            if (response.status === 200) {
                setContactedCreators(
                    creators.map((creator) => {
                        if (creator.id === response.data.id) {
                            return mapMicrowaveInviteToBaseInvite(
                                response.data,
                                influencers.find((i) => i.id === response.data.influencer_id)
                            );
                        }

                        return creator;
                    })
                );
            }

            return {
                success: response.status === 200,
                error:
                    response.status === 200
                        ? undefined
                        : response.status === 400
                          ? String(response.data.deadline)
                          : response.data.detail,
            };
        },
        [creators, influencers]
    );

    return {
        creators,
        count: creatorsCount,
        loading: creatorsLoading,
        errorLoading,
        isInitialized,
        updatePostInvite,
        reset: () => {
            setContactedCreators([]);
            setCreatorsCount(0);
            setCreatorsLoading(false);
            setErrorLoading(false);
            setIsInitialized(false);
        },
    };
}
